import { createSetupTokenClient, getSetupToken, removeSetupToken } from "@/domain/_shared/auth/services/auth.service";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { setAccessToken } from "@/services/storage.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { enable2FAForSetupMutationDocument, enable2FAMutationDocument } from "./two-factor-auth.gql";

export interface Enable2FAInput {
  code: string;
}

export interface Enable2FAResponse {
  success: boolean;
  backupCodes: string[];
}

interface Enable2FAForSetupResponse {
  status: string;
  accessToken: string;
  backupCodes: string[];
}

export interface UseEnable2FAProps {
  onCompleted?: (data: Enable2FAResponse) => void;
  onError?: (e: Error) => void;
}

/**
 * Enables 2FA for the current user. Auto-routes between the authenticated and
 * the forced-setup endpoints based on whether the user holds a 2fa_setup token.
 * On the setup path, success returns the access token directly: we stash it and
 * clear the setup token so the next render is a normal authenticated session.
 */
export const useEnable2FA = ({ onCompleted, onError }: UseEnable2FAProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: Enable2FAInput): Promise<Enable2FAResponse> => {
      if (getSetupToken()) {
        const client = await createSetupTokenClient("enable2FAForSetup");
        const data = await client.request<{ enable2FAForSetup: Enable2FAForSetupResponse }>(
          enable2FAForSetupMutationDocument,
          { input },
        );
        setAccessToken(data.enable2FAForSetup.accessToken);
        removeSetupToken();
        return { success: true, backupCodes: data.enable2FAForSetup.backupCodes };
      }
      const client = createOperationClient("enable2FA");
      const data = await client.request(enable2FAMutationDocument, { input });
      return data.enable2FA;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["getUser2FAStatus"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      onCompleted?.(data);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });

  return {
    enable2FA: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
